import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from 'lucide-react';
export function TermsAccordion() {
  const [openSection, setOpenSection] = useState<string | null>(null);
  const sections = [{
    id: 'services',
    title: '1. Services',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-994">1.1 Service Description</h4>
          <p className="mb-4" data-id="element-995">
            We provide web design, development, e-commerce, hosting,
            maintenance, and related digital services as specified in the
            project proposal or service agreement provided to you.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-996">1.2 Project Scope</h4>
          <p data-id="element-997">
            The scope of services will be defined in a written proposal,
            statement of work, or service agreement. Any services not explicitly
            included in these documents are considered out of scope and may
            require additional fees if requested.
          </p>
        </>
  }, {
    id: 'client-responsibilities',
    title: '2. Client Responsibilities',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-998">2.1 Provision of Materials</h4>
          <p className="mb-4" data-id="element-999">
            You are responsible for providing all necessary content,
            information, and materials required for your project within the
            agreed timeframes. Delays in providing these materials may affect
            project timelines and may incur additional costs.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-1000">2.2 Timely Communication</h4>
          <p className="mb-4" data-id="element-1001">
            You agree to respond to communications and provide feedback in a
            timely manner. Prolonged delays in communication (exceeding 14 days)
            may result in project delays and potential additional costs.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-1002">2.3 Review and Approval</h4>
          <p data-id="element-1003">
            You are responsible for reviewing and approving deliverables at each
            stage of the project. Once approved, any changes may incur
            additional costs.
          </p>
        </>
  }, {
    id: 'payment-terms',
    title: '3. Payment Terms',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-1004">3.1 Fees and Invoicing</h4>
          <p className="mb-3" data-id="element-1005">
            Fees for services will be outlined in the project proposal or
            service agreement. Unless otherwise specified:
          </p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-1006">
            <li className="flex items-start" data-id="element-1007">
              <span className="mr-2" data-id="element-1008">•</span>
              <span data-id="element-1009">
                A 50% non-refundable deposit is required to commence work
              </span>
            </li>
            <li className="flex items-start" data-id="element-1010">
              <span className="mr-2" data-id="element-1011">•</span>
              <span data-id="element-1012">
                The remaining balance is due upon project completion, prior to
                website launch or handover
              </span>
            </li>
            <li className="flex items-start" data-id="element-1013">
              <span className="mr-2" data-id="element-1014">•</span>
              <span data-id="element-1015">
                For ongoing services, payment is due according to the billing
                cycle specified in the service agreement
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-1016">3.2 Payment Methods</h4>
          <p className="mb-4" data-id="element-1017">
            We accept payment via bank transfer, credit/debit card, and PayPal.
            All payments must be made in GBP (British Pounds).
          </p>
          <h4 className="font-semibold mb-3" data-id="element-1018">3.3 Late Payments</h4>
          <p data-id="element-1019">
            Invoices not paid within 14 days of the due date may incur a late
            payment fee of 5% of the outstanding amount. We reserve the right to
            suspend services until payment is received.
          </p>
        </>
  }, {
    id: 'intellectual-property',
    title: '4. Intellectual Property',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-1020">4.1 Client Content</h4>
          <p className="mb-4" data-id="element-1021">
            You retain ownership of all content, logos, trademarks, and
            materials you provide to us. You represent and warrant that you own
            or have permission to use all content provided for your project.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-1022">4.2 Our Work Product</h4>
          <p className="mb-3" data-id="element-1023">
            Upon receipt of full payment, you will own the final design and
            content created specifically for your project, except for:
          </p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-1024">
            <li className="flex items-start" data-id="element-1025">
              <span className="mr-2" data-id="element-1026">•</span>
              <span data-id="element-1027">
                Third-party elements (e.g., stock photos, plugins, themes) which
                are subject to their respective licenses
              </span>
            </li>
            <li className="flex items-start" data-id="element-1028">
              <span className="mr-2" data-id="element-1029">•</span>
              <span data-id="element-1030">Our proprietary tools, frameworks, and methodologies</span>
            </li>
            <li className="flex items-start" data-id="element-1031">
              <span className="mr-2" data-id="element-1032">•</span>
              <span data-id="element-1033">
                Code libraries and reusable components developed by us
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-1034">4.3 Portfolio Rights</h4>
          <p data-id="element-1035">
            We reserve the right to display and link to your completed project
            as part of our portfolio and to write about the project for
            promotional purposes, unless explicitly agreed otherwise in writing.
          </p>
        </>
  }, {
    id: 'timeline-delivery',
    title: '5. Project Timeline and Delivery',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-1036">5.1 Estimated Timeline</h4>
          <p className="mb-4" data-id="element-1037">
            Project timelines provided are estimates based on the agreed scope
            of work. Actual completion times may vary based on project
            complexity, client feedback turnaround, and other factors.
          </p>
          <h4 className="font-semibold mb-3" data-id="element-1038">5.2 Delays</h4>
          <p className="mb-3" data-id="element-1039">We are not responsible for delays caused by:</p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-1040">
            <li className="flex items-start" data-id="element-1041">
              <span className="mr-2" data-id="element-1042">•</span>
              <span data-id="element-1043">
                Client's failure to provide necessary materials or feedback in a
                timely manner
              </span>
            </li>
            <li className="flex items-start" data-id="element-1044">
              <span className="mr-2" data-id="element-1045">•</span>
              <span data-id="element-1046">Changes to the project scope or requirements</span>
            </li>
            <li className="flex items-start" data-id="element-1047">
              <span className="mr-2" data-id="element-1048">•</span>
              <span data-id="element-1049">Third-party services or integrations</span>
            </li>
            <li className="flex items-start" data-id="element-1050">
              <span className="mr-2" data-id="element-1051">•</span>
              <span data-id="element-1052">Events beyond our reasonable control</span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-1053">5.3 Acceptance</h4>
          <p data-id="element-1054">
            Upon project completion, you will have 14 days to report any issues
            or request minor adjustments covered by the project scope. After
            this period, the project will be deemed accepted.
          </p>
        </>
  }, {
    id: 'warranties',
    title: '6. Warranties and Limitations',
    content: <>
          <h4 className="font-semibold mb-3" data-id="element-1055">6.1 Our Warranty</h4>
          <p className="mb-3" data-id="element-1056">We warrant that:</p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-1057">
            <li className="flex items-start" data-id="element-1058">
              <span className="mr-2" data-id="element-1059">•</span>
              <span data-id="element-1060">Services will be performed in a professional manner</span>
            </li>
            <li className="flex items-start" data-id="element-1061">
              <span className="mr-2" data-id="element-1062">•</span>
              <span data-id="element-1063">
                Deliverables will substantially conform to the agreed
                specifications
              </span>
            </li>
            <li className="flex items-start" data-id="element-1064">
              <span className="mr-2" data-id="element-1065">•</span>
              <span data-id="element-1066">
                The website will be compatible with current versions of major
                browsers at the time of delivery
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-1067">6.2 Disclaimer</h4>
          <p className="mb-3" data-id="element-1068">We do not warrant that:</p>
          <ul className="space-y-2 ml-4 mb-4" data-id="element-1069">
            <li className="flex items-start" data-id="element-1070">
              <span className="mr-2" data-id="element-1071">•</span>
              <span data-id="element-1072">The website will be error-free or uninterrupted</span>
            </li>
            <li className="flex items-start" data-id="element-1073">
              <span className="mr-2" data-id="element-1074">•</span>
              <span data-id="element-1075">
                The website will be compatible with all devices, browsers, or
                future technology
              </span>
            </li>
            <li className="flex items-start" data-id="element-1076">
              <span className="mr-2" data-id="element-1077">•</span>
              <span data-id="element-1078">
                The website will achieve specific business results or rankings
              </span>
            </li>
          </ul>
          <h4 className="font-semibold mb-3" data-id="element-1079">6.3 Limitation of Liability</h4>
          <p data-id="element-1080">
            Our total liability for any claims under these Terms shall not
            exceed the amount paid by you for the services. We shall not be
            liable for indirect, incidental, consequential, or special damages
            arising out of or in connection with the services.
          </p>
        </>
  }, {
    id: 'contact',
    title: '7. Contact Information',
    content: <>
          <p className="mb-4" data-id="element-1081">
            If you have any questions about these Terms, please contact us at:
          </p>
          <p className="mb-2" data-id="element-1082">
            Email:{' '}
            <a href="mailto:legal@bright-pixxel.com" className="text-[#0C1B26] hover:underline" data-id="element-1083">
              legal@bright-pixxel.com
            </a>
          </p>
          <p data-id="element-1084">Phone: +44 (0) 123 456 7890</p>
        </>
  }];
  const toggleSection = (sectionId: string) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };
  return <div className="space-y-4" data-id="element-1085">
      {sections.map(section => <div key={section.id} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-1086">
          <button onClick={() => toggleSection(section.id)} className={`w-full p-4 flex items-center justify-between text-left transition-colors ${openSection === section.id ? 'bg-[#0C1B26]/10 dark:bg-[#0C1B26]/20' : 'bg-white dark:bg-[#1E1E1E]'}`} data-id="element-1087">
            <h3 className="text-lg font-semibold" data-id="element-1088">{section.title}</h3>
            <ChevronDownIcon size={20} className={`transition-transform ${openSection === section.id ? 'rotate-180' : ''}`} data-id="element-1089" />
          </button>
          <AnimatePresence data-id="element-1090">
            {openSection === section.id && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="overflow-hidden" data-id="element-1091">
                <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700" data-id="element-1092">
                  <div className="text-gray-600 dark:text-gray-300" data-id="element-1093">
                    {section.content}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>)}
    </div>;
}